<script lang="ts">
  import { Command } from "cmdk-sv";
  import type { SvelteComponent } from "svelte";
  import * as Dialog from "$lib/components/ui/dialog";
  import { hotkeysStore } from "$lib/stores/hotkeysStore";
  import { showKeyboardShortcuts } from "$lib/stores/keyboardShortcutsStore";
  import { hotkeyActionsStore } from "$lib/utility/hotkeys";
  import type { HotkeyAction } from "$lib/utility/hotkeys";

  let mounted = $state(false);
  let search = $state("");
  let dialogRef = $state<SvelteComponent | null>(null);

  let filteredActions = $derived(() => {
    if (!mounted) return [];

    return $hotkeyActionsStore.filter((action) => {
      const visible = !action.isVisible || action.isVisible();
      const enabled = !action.isEnabled || action.isEnabled();
      const matches = fuzzyMatch(action, search);
      return visible && enabled && matches;
    });
  });

  $effect(() => {
    mounted = true;
  });

  function handleSelect(action: () => void) {
    return () => {
      action();
      hotkeysStore.setOpen(false);
    };
  }

  function handleOpenChange(open: boolean) {
    hotkeysStore.setOpen(open);
    if (open) {
      showKeyboardShortcuts.set(false);
      search = "";
      requestAnimationFrame(() => {
        const inputElement = dialogRef?.querySelector("[data-cmdk-input]") as HTMLInputElement;
        if (inputElement) {
          inputElement.focus();
        }
      });
    }
  }

  function fuzzyMatch(item: HotkeyAction, searchTerm: string): boolean {
    if (!searchTerm) return true;
    const target = item.description.toLowerCase();
    const chars = searchTerm.toLowerCase().split("");
    let currentIndex = 0;
    for (const char of chars) {
      currentIndex = target.indexOf(char, currentIndex);
      if (currentIndex === -1) return false;
      currentIndex += 1;
    }
    return true;
  }
</script>

<Dialog.Root open={$hotkeysStore.open} onOpenChange={handleOpenChange}>
  <Dialog.Content class="bg-theme-background-primary" bind:this={dialogRef}>
    <Command.Root shouldFilter={false} class="command-menu">
      <Command.Input
        class="border-0 ring-0 focus:border-0 focus:outline-none focus:ring-0"
        placeholder="Type a command..."
        bind:value={search}
      />
      <Command.List>
        {#if filteredActions().length === 0}
          <Command.Empty class="px-4">No results found.</Command.Empty>
        {:else}
          <Command.Group>
            {#each filteredActions() as hotkey (hotkey.id)}
              <Command.Item value={hotkey.description} onSelect={handleSelect(hotkey.action)}>
                <span>{hotkey.description}</span>
                {#if hotkey.keys}
                  <span class="hotkey-visual">{hotkey.keys}</span>
                {/if}
              </Command.Item>
            {/each}
          </Command.Group>
        {/if}
      </Command.List>
    </Command.Root>
  </Dialog.Content>
</Dialog.Root>

<style>
  :global([data-cmdk-item].disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }

  :global([data-cmdk-dialog]) {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: var(--background-primary);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    width: 90%;
    max-width: 600px;
    overflow: hidden;
  }

  :global([data-cmdk-overlay]) {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
    z-index: 999;
  }

  :global([data-cmdk-input]) {
    width: 100%;
    padding: 1rem;
    font-size: 1rem;
    background: var(--background-primary);
    color: var(--text-primary);
    border-bottom: 1px solid var(--border);
    box-sizing: border-box;
  }

  :global([data-cmdk-list]) {
    max-height: 300px;
    overflow-y: auto;
    background: var(--background-primary);
  }

  :global([data-cmdk-item]) {
    padding: 0.75rem 1rem;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--text-primary);
    transition: background-color 150ms ease;
  }

  :global([data-cmdk-item][data-selected="true"]) {
    background-color: var(--background-secondary);
  }

  :global([data-cmdk-group-heading]) {
    padding: 0.5rem 1rem;
    font-weight: bold;
    color: var(--text-secondary);
  }

  :global(.hotkey-visual) {
    color: var(--text-secondary);
    font-size: 0.875em;
    padding: 0.125rem 0.375rem;
    background: var(--background-tertiary);
    border-radius: 0.25rem;
  }

  :global([data-cmdk-list]::-webkit-scrollbar) {
    width: 4px;
  }

  :global([data-cmdk-list]::-webkit-scrollbar-thumb) {
    background-color: var(--gray-600);
    border-radius: 4px;
  }

  :global([data-cmdk-list]::-webkit-scrollbar-track) {
    background-color: var(--background-secondary);
  }
</style>
